export const paymentOptions = ["any", "paid", "pending", "authorized", "partially_paid", "partially_refunded", "refunded", "voided", "expired"] as const;
export const fulfillmentOptions = ["any", "unfulfilled", "partial", "fulfilled", "on_hold", "scheduled", "request_declined"] as const;
export type ImportSelection = {
  mode: "recent" | "dates";
  from: string;
  through: string;
  payment: typeof paymentOptions[number];
  fulfillment: typeof fulfillmentOptions[number];
};
export type ImportSelectionReview = {
  selection: ImportSelection;
  window_start: string;
  window_end: string;
  history_access: boolean;
  reviewed_at: string;
  token: string;
};
export const defaultImportSelection: ImportSelection = { mode: "recent", from: "", through: "", payment: "any", fulfillment: "any" };
export function importSelectionDraft(value: unknown): value is ImportSelection {
  const v = value as ImportSelection;
  return !!v && !Array.isArray(v) && ["recent", "dates"].includes(v.mode) && typeof v.from === "string" && v.from.length <= 10 && typeof v.through === "string" && v.through.length <= 10 && paymentOptions.includes(v.payment) && fulfillmentOptions.includes(v.fulfillment);
}
function date(value: unknown) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value < "2000-01-01") throw new Error("Choose a valid date on or after 1 January 2000");
  const parsed = new Date(value + "T00:00:00.000Z");
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) throw new Error("Choose a valid calendar date");
  return value;
}
export function parseImportSelection(value: unknown): ImportSelection {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Choose the order import dates and statuses");
  const v = value as Record<string, unknown>;
  if (!Object.keys(v).every(k => ["mode", "from", "through", "payment", "fulfillment"].includes(k)) || !["recent", "dates"].includes(String(v.mode)) || !paymentOptions.includes(v.payment as ImportSelection["payment"]) || !fulfillmentOptions.includes(v.fulfillment as ImportSelection["fulfillment"])) throw new Error("Choose valid order import dates and statuses");
  const from = v.mode === "dates" ? date(v.from) : "", through = v.mode === "dates" ? date(v.through) : "";
  if (from > through) throw new Error("The end date must be on or after the start date");
  if (v.mode === "recent" && (v.from !== "" || v.through !== "")) throw new Error("Recent imports cannot also specify calendar dates");
  return { mode: v.mode as ImportSelection["mode"], from, through, payment: v.payment as ImportSelection["payment"], fulfillment: v.fulfillment as ImportSelection["fulfillment"] };
}
export function selectionWindow(selection: ImportSelection, now = Date.now()) {
  const start = selection.mode === "recent" ? now - 59 * 86400000 : Date.parse(selection.from + "T00:00:00.000Z");
  const end = selection.mode === "recent" ? now : Math.min(Date.parse(selection.through + "T23:59:59.999Z"), now);
  if (selection.mode === "dates" && selection.through > new Date(now).toISOString().slice(0, 10)) throw new Error("Import dates cannot be in the future");
  return { window_start: new Date(start).toISOString(), window_end: new Date(end).toISOString() };
}
export function parseSelectionReview(value: unknown): ImportSelectionReview {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Review the selected import first");
  const v = value as ImportSelectionReview, selection = parseImportSelection(v.selection);
  if (typeof v.token !== "string" || v.token.length < 80 || v.token.length > 6000 || typeof v.history_access !== "boolean" || typeof v.reviewed_at !== "string" || !Number.isFinite(Date.parse(v.reviewed_at))) throw new Error("Review the selected import again");
  const window = selectionWindow(selection, Date.parse(v.reviewed_at));
  if (v.window_start !== window.window_start || v.window_end !== window.window_end) throw new Error("The reviewed import dates changed");
  return { selection, ...window, history_access: v.history_access, reviewed_at: v.reviewed_at, token: v.token };
}
export function selectionLabel(value: Pick<ImportSelection, "payment" | "fulfillment">) {
  return `Payment: ${value.payment.replace(/_/g, " ")}. Fulfillment: ${value.fulfillment === "partial" ? "partially fulfilled" : value.fulfillment.replace(/_/g, " ")}.`;
}
